/**
 * Saldo VES (cardex de bolívares) por tipo de documento. Misma regla que Nest
 * (`wallet-ves/saldo-ves.ts` → `puedeTenerSaldoVes`, que usa
 * `familiaDocumento`):
 *  - familia VE (VE, CCVE, V): saldo VES completo, con entradas y salidas
 *    (la recarga por depósito, solo `tipoId === 'VE'`: `puedeRecargarVes`);
 *  - familia CL (RUT, RUN, CL, RUT EMPRESA): puede TENER saldo VES (verlo,
 *    comprarlo y guardarlo) y sacarlo por las mismas pantallas del VE (retiro
 *    VES, VES→USDT, VES→CLP). Las remesas VES→COP/PEN (Vita) no se le
 *    ofrecen. 30/10/2026: no lo recarga (ver `puedeRecargarVes`).
 *  - tipo vacío o desconocido: cuenta como VE si la cédula tiene formato V/E
 *    (`V12345678`, `E-12345678`; sin puntos ni espacios). Igual que el back.
 *
 * No reemplaza los `tipoId === 'VE'` que significan "moneda local" (tarjeta
 * CLP, menú CLP, etc.): el chileno conserva todo lo CLP y suma lo VES.
 */
const FAMILIA_VE = ['VE', 'CCVE', 'V'];
const FAMILIA_CL = ['RUT', 'RUN', 'CL', 'RUT EMPRESA'];

const normalizar = (tipoId?: string | null): string => String(tipoId ?? '').trim().toUpperCase();

/** Cédula con formato venezolano (V/E + 6 a 10 dígitos), como `familiaDocumento` del back. */
const esCedulaVenezolana = (numeroId?: string | null): boolean =>
  /^[VE]-?\d{6,10}$/i.test(String(numeroId ?? '').replace(/[.\s]/g, ''));

/** Familia del documento: 'CL', 'VE' o null (mismo orden que el back: primero el tipo). */
export function familiaSaldoVes(tipoId?: string | null, numeroId?: string | null): 'CL' | 'VE' | null {
  const tipo = normalizar(tipoId);
  if (FAMILIA_CL.includes(tipo)) return 'CL';
  if (FAMILIA_VE.includes(tipo)) return 'VE';
  return esCedulaVenezolana(numeroId) ? 'VE' : null;
}

/**
 * Titular venezolano: su moneda local es VES (textos y flujos "locales").
 * Con `numeroId`, un tipo vacío/desconocido con cédula V/E también cuenta.
 */
export function esTitularVesVenezolano(tipoId?: string | null, numeroId?: string | null): boolean {
  return familiaSaldoVes(tipoId, numeroId) === 'VE';
}

/** Chileno con saldo VES (agrega lo VES sin quitar lo CLP). */
export function esTitularVesChileno(tipoId?: string | null): boolean {
  return FAMILIA_CL.includes(normalizar(tipoId));
}

/** Tiene saldo VES: familia VE o familia CL (o tipo desconocido con cédula V/E). */
export function tieneSaldoVes(tipoId?: string | null, numeroId?: string | null): boolean {
  return familiaSaldoVes(tipoId, numeroId) !== null;
}

/**
 * Recarga de bolívares: depositar en las cuentas VES de Aguacate e informar la
 * referencia (`rechargeAccount` en modo VES y «Depósitos VES»). Solo el
 * `tipoId === 'VE'` exacto, como antes de que el chileno tuviera saldo VES.
 *
 * 30/10/2026: el chileno no recarga bolívares. Tampoco CCVE/V ni un tipo vacío
 * con cédula V/E, que usan el menú y la recarga CLP. Al chileno los bolívares
 * le entran comprándolos con CLP (`comprar-bolivares`) o desde USDT
 * (`convert-usdt-ves`). Es un candado de pantalla: hoy Nest
 * (`ReferenciasVesService.create`) acepta la referencia de cualquiera que
 * `puedeTenerSaldoVes`.
 */
export function puedeRecargarVes(tipoId?: string | null): boolean {
  return tipoId === 'VE';
}

/**
 * Beneficiario de un banco venezolano (`banco.codigoPais === 'VE'`, el campo
 * que trae `recipient/fiat/account-recipient`). Es lo único que acepta el
 * retiro VES de Nest (`retiroVes` responde 400 con otro país): el chileno
 * también tiene beneficiarios de otros países para sus envíos CLP.
 */
export function esBeneficiarioBancoVenezolano(
  cuenta?: { banco?: { codigoPais?: string | null } | null } | null,
): boolean {
  return String(cuenta?.banco?.codigoPais ?? '').trim().toUpperCase() === 'VE';
}
